import cn from "classnames"
import { Fragment, useMemo } from "react"

import { Price } from "./Price"
import { Header, HeaderContent } from "./Header"
import { typeOfAvailability, getDefaultAvailability } from "./utils"
import style from "./style.module.scss"

import { renderCityWithDetails } from "utils/helpers/psychologistUtil"
import { HelpTooltip, Icon } from "components"
import { Analytics } from "utils/analytics"
import { useCurrentStatus } from "app/AppRouter/CurrentStatusContext"
import { APP_STATUSES } from "utils/AppStatusContext"
import { Psychologist } from "utils/interfaces"
import { ProfileLink } from "app/AppRouter/links"
import { useReportPsychologistNameCopy, useTabletOrMobile } from "utils/hooks"
import { useSearchParams } from "utils/customHooks"
import { getLocationPath } from "utils/helpers"
import { useShowPrice } from "utils/hooks/useShowPrice"
import { CompetenceTitle } from "components/Cards/components/Card/components/CompetenceTitle"
import { getCommonCompetence } from "components/Cards/components/Card/utils"
import { SurveyResultsStorage } from "modules/NewClientSurvey/common/utils/SurveyResultsStorage"

function getProfileLinkTarget(isTabletOrMobile: boolean, currentStatus: APP_STATUSES) {
    if (currentStatus === APP_STATUSES.search_page) return "_blank"
    return isTabletOrMobile ? undefined : "_blank"
}

interface Props {
    psy: Psychologist
    index: string
    labelsBlockMaxHeight?: string
    freeBooking?: boolean
}

export function Card({ psy, index, labelsBlockMaxHeight = "", freeBooking }: Props) {
    const isTabletOrMobile = useTabletOrMobile()
    const currentStatus = useCurrentStatus()
    const searchParams = useSearchParams()
    const isShowPrice = useShowPrice()

    useReportPsychologistNameCopy(psy.display_name, "survey_card")

    const target = freeBooking ? undefined : getProfileLinkTarget(isTabletOrMobile, currentStatus)

    const resultsKey = searchParams.get("results_key")

    const methods = useMemo(() => psy.methods.slice(0, 2), [psy.methods])
    const pathName = getLocationPath()
    const commonCompetenceNumber = getCommonCompetence(
        psy.competences,
        SurveyResultsStorage.reportedProblemsId || []
    )

    return (
        <section className={style.container}>
            <div className={style.top}>
                <div>
                    <div>
                        {freeBooking ? (
                            <HeaderContent
                                psy={psy}
                                freeBooking={freeBooking}
                                classes={{
                                    labels: labelsBlockMaxHeight,
                                }}
                            />
                        ) : (
                            <Header
                                psy={psy}
                                index={index}
                                target={target}
                                from={APP_STATUSES.survey_results}
                                classes={{
                                    labels: labelsBlockMaxHeight,
                                }}
                            />
                        )}
                    </div>

                    {freeBooking ? (
                        <>
                            <hr className="mgt-4 mgb-3" />
                            <div className="mgb-3 font-weight-bolder">Бесплатно</div>
                        </>
                    ) : (
                        <div>
                            <CompetenceTitle competence={psy.competences} />
                            {isShowPrice && (
                                <Price psy={psy} hasBorderTop={!commonCompetenceNumber} />
                            )}
                            {methods.length > 0 && (
                                <div
                                    className={cn(style.methods, {
                                        [style["is-multi"]]: methods.length > 1,
                                    })}
                                >
                                    <div className={style.methods__title}>
                                        {methods.length > 1 ? "Методы" : "Основной метод"}
                                    </div>
                                    <div className={style.methods__list}>
                                        {methods.map((item, index) => {
                                            return (
                                                <Fragment key={item.code}>
                                                    {index === 1 && (
                                                        <div className={style.methods__separator}>
                                                            &nbsp;·&nbsp;
                                                        </div>
                                                    )}
                                                    <span className={style.methods__item}>
                                                        <span className={style.methods__itemName}>
                                                            {item.short_name}
                                                        </span>
                                                        <span
                                                            className={cn(
                                                                style.methods__tooltip,
                                                                "ml-1"
                                                            )}
                                                        >
                                                            <HelpTooltip
                                                                placement="bottom"
                                                                overlay={
                                                                    <>
                                                                        <div
                                                                            className={
                                                                                style.methods__tooltipName
                                                                            }
                                                                        >
                                                                            {item.name}
                                                                        </div>
                                                                        <div
                                                                            className={
                                                                                style.methods__tooltipDescription
                                                                            }
                                                                        >
                                                                            {item.description}
                                                                        </div>
                                                                    </>
                                                                }
                                                            />
                                                        </span>
                                                    </span>
                                                </Fragment>
                                            )
                                        })}
                                    </div>
                                </div>
                            )}
                            <div className={style.location}>
                                <div className={style.location__title}>Где ведет прием</div>
                                <div className={style.location__card}>
                                    <Icon type="nav" mr={2} color="blue-3" size={24} />
                                    {typeOfAvailability(
                                        psy.available_for_booking,
                                        getDefaultAvailability(psy.offered_services)
                                    ) === "Принимает онлайн" ? (
                                        <span>{psy.new_city.name}</span>
                                    ) : (
                                        renderCityWithDetails(psy.new_city.name, psy.city_details)
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <footer className={cn(style.footer, "is-flex is-flex-direction-column")}>
                {!freeBooking && (
                    <ProfileLink
                        position={index}
                        profileUrl={psy.profile_url}
                        onClick={() => {
                            Analytics.to_profile_clicked()
                        }}
                        className={cn(style.footer__buttonMore, "button-primary")}
                        target={target}
                        from={APP_STATUSES.survey_results}
                        resultsKey={resultsKey ?? undefined}
                        fromLink={pathName}
                    >
                        Подробнее
                    </ProfileLink>
                )}
            </footer>
        </section>
    )
}
